import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { conversationsService, Conversation } from '@/services/conversations';
import notify from '@/lib/notify';
import { Button } from '@/components/Common/button';
import { PenLine, TrashIcon } from 'lucide-react';

interface ConversationsSidebarProps {
  currentConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
  chatbotId?: string;
  refreshToken?: number;
}

export const ConversationsSidebar: React.FC<ConversationsSidebarProps> = ({
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  chatbotId,
  refreshToken = 0,
}) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId, refreshToken]);

  const loadConversations = async () => {
    setIsLoading(true);
    try {
      const data = await conversationsService.list(chatbotId);
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
      notify.error(t('chatPage.loadConversationsError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(t('conversationsPage.confirmDelete'))) return;

    try {
      await conversationsService.delete(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      notify.success(t('conversationsPage.deleted'));
      if (currentConversationId === id) {
        onNewConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      notify.error(t('conversationsPage.deleteError'));
    }
  };

  return (
    <aside className="flex h-full min-h-0 w-[260px] shrink-0 flex-col border-r border-border/80 bg-muted/20">
      <div className="shrink-0 p-2">
        <Button
          variant="outline"
          className="h-10 w-full justify-start gap-2 rounded-lg border-border/80 bg-background/60 text-sm font-normal shadow-none hover:bg-muted/80"
          onClick={onNewConversation}
          aria-label={t('chatPage.newChatAria')}
        >
          <PenLine className="h-4 w-4 shrink-0 opacity-70" />
          {t('aiChatbot.newConversation')}
        </Button>
      </div>

      <p className="px-3 pb-1 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {t('chatPage.yourChats')}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {isLoading ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            {t('chatPage.loadingConversations')}
          </div>
        ) : conversations.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            {t('chatPage.noConversationsYet')}
          </div>
        ) : (
          <div className="space-y-0.5">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectConversation(conversation.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectConversation(conversation.id);
                  }
                }}
                className={`
                  group flex cursor-pointer rounded-lg px-2.5 py-2.5 text-left transition-colors
                  hover:bg-muted/70
                  ${currentConversationId === conversation.id ? 'bg-muted/90' : ''}
                `}
              >
                <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm leading-snug text-foreground">{conversation.title}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {new Date(conversation.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(conversation.id, e)}
                    className="shrink-0 rounded p-1 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    aria-label={t('conversationsPage.deleteAria')}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};
